import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  Activity,
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CheckCircle2,
  Clock,
  Database,
  Gauge,
  PauseCircle,
  PlayCircle,
  RefreshCw,
  Server,
  ShieldAlert,
  Wifi,
  WifiOff,
} from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import {
  getAdminInstagramCollabOps,
  getAdminMetaOperations,
  refreshAdminInstagramCollabOps,
  refreshAdminInstagramPostOps,
  updateAdminMetaOperationsPause,
} from '../../services/api';
import AdminPageHeader from './AdminPageHeader';
import './AdminStyles.scss';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 15 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.23, 1, 0.32, 1] } },
};

const statusClass = (status) => ({
  healthy: 'dashboard-view__pill--green',
  warning: 'dashboard-view__pill--orange',
  slowdown: 'dashboard-view__pill--orange',
  critical: 'dashboard-view__pill--red',
  cooldown: 'dashboard-view__pill--red',
  unknown: 'dashboard-view__pill--blue',
}[status] || 'dashboard-view__pill--blue');

const formatDuration = (seconds, t) => {
  if (seconds === null || seconds === undefined) return t('common.notAvailable');
  if (seconds < 60) return t('time.seconds', { count: Math.max(0, Math.floor(seconds)) });
  if (seconds < 3600) return t('time.minutes', { count: Math.floor(seconds / 60) });
  return t('time.hours', { count: Math.floor(seconds / 3600) });
};

const secondsUntil = (value, now) => {
  if (!value) return null;
  return Math.max(0, Math.floor((new Date(value).getTime() - now) / 1000));
};

const apiErrorMessage = (error, fallback) => error?.response?.data?.message || fallback;

const MetaManagement = () => {
  const { t, i18n } = useTranslation('admin');
  const { currentUser, loading: authLoading } = useAuth();
  const locale = i18n.resolvedLanguage || 'es';

  const [loading, setLoading] = useState(true);
  const [opsData, setOpsData] = useState(null);
  const [opsError, setOpsError] = useState('');
  const [selectedCollabId, setSelectedCollabId] = useState(null);
  const [collabOpsDetail, setCollabOpsDetail] = useState(null);
  const [collabOpsLoading, setCollabOpsLoading] = useState(false);
  const [collabRefreshLoading, setCollabRefreshLoading] = useState(false);
  const [collabRefreshMessage, setCollabRefreshMessage] = useState('');
  const [collabRefreshError, setCollabRefreshError] = useState('');
  const [refreshLimit, setRefreshLimit] = useState(10);
  const [pauseReason, setPauseReason] = useState('');
  const [pauseDuration, setPauseDuration] = useState(60);
  const [pauseLoading, setPauseLoading] = useState(false);
  const [sort, setSort] = useState({ key: 'calls', direction: 'desc' });
  const [now, setNow] = useState(Date.now());
  const [confirmation, setConfirmation] = useState(null);

  const fetchOpsData = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      setOpsError('');
      const response = await getAdminMetaOperations();
      setOpsData(response.data);
    } catch (error) {
      setOpsError(apiErrorMessage(error, t('errors.dashboard')));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (!currentUser?.is_staff && !currentUser?.is_superuser) return undefined;
    fetchOpsData();
    const polling = window.setInterval(() => fetchOpsData({ silent: true }), 30000);
    return () => window.clearInterval(polling);
  }, [currentUser, fetchOpsData]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const fetchCollabOpsDetail = async (collabId) => {
    try {
      setSelectedCollabId(collabId);
      setCollabOpsLoading(true);
      setCollabRefreshMessage('');
      setCollabRefreshError('');
      const response = await getAdminInstagramCollabOps(collabId);
      setCollabOpsDetail(response.data);
    } catch (error) {
      setCollabRefreshError(apiErrorMessage(error, t('errors.collabDetail')));
    } finally {
      setCollabOpsLoading(false);
    }
  };

  const handleRefreshCollabOps = async (mode = 'all', confirmed = false) => {
    if (!selectedCollabId) return;
    const confirmationMsg = mode === 'errors'
      ? t('controls.confirmErrors', { count: refreshLimit })
      : t('controls.confirmCampaign', { count: refreshLimit });
    if (!confirmed) {
      setConfirmation({ message: confirmationMsg, onConfirm: () => handleRefreshCollabOps(mode, true) });
      return;
    }
    try {
      setCollabRefreshLoading(true);
      setCollabRefreshError('');
      const response = await refreshAdminInstagramCollabOps(selectedCollabId, refreshLimit, mode);
      setCollabOpsDetail(response.data.detail);
      setCollabRefreshMessage(t('controls.queued', { count: response.data.queued, calls: response.data.estimated_calls }));
      await fetchOpsData({ silent: true });
    } catch (error) {
      setCollabRefreshError(apiErrorMessage(error, t('errors.queueRefresh')));
    } finally {
      setCollabRefreshLoading(false);
    }
  };

  const handleRefreshPost = async (postId, confirmed = false) => {
    if (!confirmed) {
      setConfirmation({ message: t('controls.confirmPost'), onConfirm: () => handleRefreshPost(postId, true) });
      return;
    }
    try {
      setCollabRefreshLoading(true);
      setCollabRefreshError('');
      const response = await refreshAdminInstagramPostOps(postId);
      setCollabOpsDetail(response.data.detail);
      setCollabRefreshMessage(t('controls.postQueued', { calls: response.data.estimated_calls }));
      await fetchOpsData({ silent: true });
    } catch (error) {
      setCollabRefreshError(apiErrorMessage(error, t('errors.queuePost')));
    } finally {
      setCollabRefreshLoading(false);
    }
  };

  const handlePause = async (action, confirmed = false) => {
    if (pauseReason.trim().length < 5) {
      setCollabRefreshError(t('controls.reasonRequired'));
      return;
    }
    if (!confirmed) {
      setConfirmation({
        message: t(action === 'pause' ? 'controls.confirmPause' : 'controls.confirmResume'),
        onConfirm: () => handlePause(action, true),
      });
      return;
    }
    try {
      setPauseLoading(true);
      setCollabRefreshError('');
      const response = await updateAdminMetaOperationsPause({
        action,
        reason: pauseReason.trim(),
        durationMinutes: action === 'pause' ? pauseDuration : undefined,
      });
      setOpsData(previous => ({ ...previous, meta_operations_control: response.data }));
      setPauseReason('');
      await fetchOpsData({ silent: true });
    } catch (error) {
      setCollabRefreshError(apiErrorMessage(error, t('errors.pause')));
    } finally {
      setPauseLoading(false);
    }
  };

  const handleSort = (key) => setSort(previous => ({
    key,
    direction: previous.key === key && previous.direction === 'desc' ? 'asc' : 'desc',
  }));

  const sortedActivity = useMemo(() => {
    const rows = opsData?.meta_api_health?.call_metrics?.activity_rows || [];
    return [...rows].sort((left, right) => {
      const a = left[sort.key];
      const b = right[sort.key];
      const comparison = typeof a === 'string' ? a.localeCompare(b) : (a || 0) - (b || 0);
      return sort.direction === 'asc' ? comparison : -comparison;
    });
  }, [opsData, sort]);

  if (authLoading || loading) return <div className="dashboard-loading"><div className="spinner" /></div>;
  if (!currentUser || (!currentUser.is_staff && !currentUser.is_superuser)) return <Navigate to="/dashboard" />;
  if (!opsData) return <div className="dashboard-view__fatal-error">{opsError || t('errors.dashboard')}</div>;

  const health = opsData.meta_api_health || {};
  const budget = health.global_budget || {};
  const totals = health.call_metrics?.totals || {};
  const refreshOps = opsData.instagram_refresh_ops || {};
  const runtime = refreshOps.runtime || {};
  const control = opsData.meta_operations_control || {};
  const estimatedCalls = health.estimated_calls_per_refresh || 1;
  const budgetPercent = budget.limit ? Math.min(100, Math.round((budget.used / budget.limit) * 100)) : 0;
  const resetSeconds = secondsUntil(budget.resets_at, now);
  const cooldownSeconds = secondsUntil(health.next_allowed_retry_at, now);
  const manualDisabled = Boolean(
    collabRefreshLoading || control.noncritical_paused || health.temporarily_limited || budget.remaining < estimatedCalls
  );
  const dateTime = value => value
    ? new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
    : t('common.never');

  const SortIcon = ({ column }) => {
    if (sort.key !== column) return <ArrowUpDown size={13} />;
    return sort.direction === 'asc' ? <ArrowUp size={13} /> : <ArrowDown size={13} />;
  };

  return (
    <div className="dashboard-page meta-management-page">
      <AdminPageHeader
        title={t('meta.title')}
        subtitle={t('meta.subtitle')}
        breadcrumbs="Meta & Instagram"
        actionButton={
          <div className="dashboard-view__panel-actions">
            <span className={`dashboard-view__pill ${statusClass(health.status)}`}>
              {t(`meta.status.${health.status || 'unknown'}`)}
            </span>
            <button
              className="dashboard-view__icon-button"
              type="button"
              onClick={() => fetchOpsData({ silent: true })}
              aria-label={t('common.refresh')}
            >
              <RefreshCw size={16} />
            </button>
          </div>
        }
      />

      <motion.div className="dashboard-view__inner" variants={containerVariants} initial="hidden" animate="visible">
        {opsError && <div className="dashboard-view__alert dashboard-view__alert--error" role="alert">{opsError}</div>}

        <div className="dashboard-view__grid">
          <div className="dashboard-view__col-12">
            <motion.section className="dashboard-view__panel dashboard-view__instagram-panel" variants={itemVariants}>
              {(health.usage_stale || control.noncritical_paused || !runtime.beat?.detected) && (
                <div className="dashboard-view__alerts">
                  {health.usage_stale && (
                    <div className="dashboard-view__alert dashboard-view__alert--warning">
                      <AlertTriangle size={16} />
                      <span>{health.captured_at ? t('meta.staleData', { age: formatDuration(health.captured_age_seconds, t) }) : t('meta.noUsageData')}</span>
                    </div>
                  )}
                  {control.noncritical_paused && (
                    <div className="dashboard-view__alert dashboard-view__alert--paused">
                      <PauseCircle size={16} />
                      <span>{t('controls.pausedUntil', { date: dateTime(control.paused_until), reason: control.reason })}</span>
                    </div>
                  )}
                  {!runtime.beat?.detected && (
                    <div className="dashboard-view__alert dashboard-view__alert--error">
                      <WifiOff size={16} />
                      <span>{t('queue.beatMissing')}</span>
                    </div>
                  )}
                </div>
              )}

              <div className="dashboard-view__ops-health-grid">
                <article className="dashboard-view__ops-health-card dashboard-view__ops-health-card--usage">
                  <div className="dashboard-view__ops-card-heading">
                    <Gauge size={18} />
                    <div><strong>{t('meta.reportedUsage')}</strong><small>{t('meta.reportedUsageHelp')}</small></div>
                  </div>
                  <div className="dashboard-view__usage-metrics">
                    {['call_count', 'total_cputime', 'total_time', 'call_volume', 'cpu_time'].map(field => (
                      <div key={field}>
                        <span>{field}</span>
                        <strong>{health.usage_breakdown?.[field] ?? '—'}{health.usage_breakdown?.[field] !== null && health.usage_breakdown?.[field] !== undefined ? '%' : ''}</strong>
                      </div>
                    ))}
                  </div>
                  <footer>{t('meta.lastCapture')}: <strong>{dateTime(health.captured_at)}</strong></footer>
                </article>

                <article className="dashboard-view__ops-health-card">
                  <div className="dashboard-view__ops-card-heading">
                    <Database size={18} />
                    <div><strong>{t('meta.internalBudget')}</strong><small>{t('meta.internalBudgetHelp')}</small></div>
                  </div>
                  <div className="dashboard-view__budget-value">
                    <strong>{budget.used || 0}</strong>
                    <span>/ {budget.limit || 0}</span>
                  </div>
                  <div className="dashboard-view__progress"><i style={{ width: `${budgetPercent}%` }} /></div>
                  <div className="dashboard-view__budget-meta">
                    <span>{t('meta.remaining')}: <strong>{budget.remaining || 0}</strong></span>
                    <span>{t('meta.resetsIn')}: <strong>{formatDuration(resetSeconds, t)}</strong></span>
                  </div>
                </article>

                <article className="dashboard-view__ops-health-card">
                  <div className="dashboard-view__ops-card-heading">
                    <ShieldAlert size={18} />
                    <div><strong>{t('meta.protection')}</strong><small>{t('meta.thresholds', health.thresholds || {})}</small></div>
                  </div>
                  <div className={`dashboard-view__cooldown-state ${health.temporarily_limited ? 'is-active' : ''}`}>
                    {health.temporarily_limited ? <PauseCircle size={24} /> : <CheckCircle2 size={24} />}
                    <div>
                      <strong>{t(health.temporarily_limited ? 'meta.cooldownActive' : 'meta.noCooldown')}</strong>
                      <span>{health.temporarily_limited ? t('meta.resumesAt', { date: dateTime(health.next_allowed_retry_at), remaining: formatDuration(cooldownSeconds, t) }) : t('meta.callsAvailable')}</span>
                    </div>
                  </div>
                </article>
              </div>

              <div className="dashboard-view__section-heading">
                <div><h3>{t('activity.title')}</h3><p>{t('activity.subtitle')}</p></div>
                <span>{t('activity.windowStarted')}: {dateTime(health.call_metrics?.window_started_at)}</span>
              </div>
              <div className="dashboard-view__ops-grid dashboard-view__ops-grid--activity">
                <div className="dashboard-view__ops-card"><CheckCircle2 size={16} /><span>{t('activity.successful')}</span><strong>{totals.successful || 0}</strong></div>
                <div className="dashboard-view__ops-card"><AlertTriangle size={16} /><span>{t('activity.errors')}</span><strong>{totals.errors || 0}</strong></div>
                <div className="dashboard-view__ops-card"><Clock size={16} /><span>{t('activity.rateLimited')}</span><strong>{totals.rate_limited || 0}</strong></div>
                <div className="dashboard-view__ops-card"><ShieldAlert size={16} /><span>{t('activity.blocked')}</span><strong>{totals.blocked || 0}</strong></div>
              </div>

              <div className="dashboard-view__activity-table-wrap">
                <table className="dashboard-view__activity-table">
                  <thead>
                    <tr>
                      {[
                        ['dimension', 'activity.dimension'], ['label', 'activity.name'], ['calls', 'activity.calls'],
                        ['successful', 'activity.successful'], ['errors', 'activity.errors'], ['rate_limited', 'activity.rateLimited'],
                        ['blocked', 'activity.blocked'], ['error_rate', 'activity.errorRate'], ['budget_percent', 'activity.budgetShare'],
                      ].map(([key, label]) => (
                        <th key={key}>
                          <button type="button" onClick={() => handleSort(key)}>{t(label)} <SortIcon column={key} /></button>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedActivity.length ? sortedActivity.map(row => (
                      <tr key={`${row.dimension}-${row.identifier}`}>
                        <td><span className="dashboard-view__dimension-badge">{t(`activity.dimensions.${row.dimension}`)}</span></td>
                        <td><strong>{row.label}</strong></td>
                        <td>{row.calls}</td>
                        <td>{row.successful}</td>
                        <td>{row.errors}</td>
                        <td>{row.rate_limited}</td>
                        <td>{row.blocked}</td>
                        <td>{row.error_rate}%</td>
                        <td>{row.budget_percent}%</td>
                      </tr>
                    )) : <tr><td colSpan="9" className="dashboard-view__empty-cell">{t('activity.empty')}</td></tr>}
                  </tbody>
                </table>
              </div>

              <div className="dashboard-view__section-heading">
                <div><h3>{t('queue.title')}</h3><p>{t('queue.subtitle')}</p></div>
              </div>
              <div className="dashboard-view__ops-grid dashboard-view__ops-grid--states">
                {[['fresh', refreshOps.fresh, CheckCircle2], ['stale', refreshOps.stale, Clock], ['frozen', refreshOps.frozen, Database], ['error', refreshOps.error, AlertTriangle]].map(([state, value, Icon]) => (
                  <div className="dashboard-view__ops-card" key={state}>
                    <Icon size={16} />
                    <span>{t(`queue.states.${state}`)}</span>
                    <strong>{value || 0}</strong>
                    <small>{t(`queue.stateHelp.${state}`)}</small>
                  </div>
                ))}
              </div>

              <div className="dashboard-view__queue-grid">
                <article className="dashboard-view__queue-card">
                  <h4>{t('queue.overdue')}</h4>
                  <div className="dashboard-view__bucket-list">
                    {[
                      ['under_15m', '< 15 min'], ['from_15m_to_1h', '15–60 min'], ['from_1h_to_6h', '1–6 h'], ['over_6h', '> 6 h'],
                    ].map(([key, label]) => (
                      <div key={key}>
                        <span>{label}</span>
                        <strong>{refreshOps.overdue_buckets?.[key] || 0}</strong>
                      </div>
                    ))}
                  </div>
                  <footer>{t('queue.oldest')}: <strong>{formatDuration((refreshOps.oldest_due_age_minutes || 0) * 60, t)}</strong></footer>
                </article>
                <article className="dashboard-view__queue-card">
                  <h4>{t('queue.runtime')}</h4>
                  <div className="dashboard-view__runtime-list">
                    <div>{runtime.beat?.detected ? <Wifi size={17} /> : <WifiOff size={17} />}<span>{t('queue.beat')}</span><strong>{dateTime(runtime.beat?.observed_at)}</strong></div>
                    <div>{runtime.worker?.detected ? <Server size={17} /> : <AlertTriangle size={17} />}<span>{t('queue.worker')}</span><strong>{dateTime(runtime.worker?.observed_at)}</strong></div>
                    <div><RefreshCw size={17} /><span>{t('queue.lastCycle')}</span><strong>{dateTime(runtime.last_scheduler_cycle?.finished_at)}</strong></div>
                    <div><Clock size={17} /><span>{t('queue.nextCycle')}</span><strong>{dateTime(runtime.next_scheduler_at)}</strong></div>
                    <div><Activity size={17} /><span>{t('queue.lastQueued')}</span><strong>{runtime.last_scheduler_cycle?.queued ?? '—'}</strong></div>
                  </div>
                </article>
              </div>

              <div className="dashboard-view__ops-split">
                <div>
                  <h3>{t('workload.campaigns')}</h3>
                  <div className="dashboard-view__ops-list">
                    {refreshOps.top_collabs?.map(collab => (
                      <button
                        key={collab.id}
                        type="button"
                        className={`dashboard-view__ops-list-item ${selectedCollabId === collab.id ? 'is-selected' : ''}`}
                        onClick={() => fetchCollabOpsDetail(collab.id)}
                      >
                        <span>{collab.title}</span>
                        <em>{t('workload.summary', { due: collab.due_refresh_count, pieces: collab.deliverables_count })}</em>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <h3>{t('workload.creators')}</h3>
                  <div className="dashboard-view__ops-list">
                    {refreshOps.top_creators?.map(creator => (
                      <div key={creator.id}>
                        <span>@{creator.username}</span>
                        <em>{t('workload.summary', { due: creator.due_refresh_count, pieces: creator.deliverables_count })}</em>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {(selectedCollabId || collabOpsDetail) && (
                <div className="dashboard-view__ops-detail">
                  {collabOpsLoading ? <p>{t('controls.loadingDetail')}</p> : collabOpsDetail && (
                    <>
                      <div className="dashboard-view__ops-detail-header">
                        <div>
                          <h3>{collabOpsDetail.offer.title}</h3>
                          <p>{t('controls.collabSummary', { due: collabOpsDetail.counts.due_now, minutes: collabOpsDetail.oldest_due_age_minutes || 0 })}</p>
                        </div>
                        <div className="dashboard-view__refresh-controls">
                          <label>
                            {t('controls.limit')}
                            <select value={refreshLimit} onChange={event => setRefreshLimit(Number(event.target.value))}>
                              {[1, 5, 10, 25].map(value => <option key={value} value={value}>{value}</option>)}
                            </select>
                          </label>
                          <span>{t('controls.estimate', { calls: refreshLimit * estimatedCalls })}</span>
                          <div>
                            <button
                              type="button"
                              className="dashboard-view__button dashboard-view__button--gold"
                              onClick={() => handleRefreshCollabOps('all')}
                              disabled={manualDisabled}
                            >
                              {collabRefreshLoading ? t('controls.queueing') : t('controls.refreshCampaign')}
                            </button>
                            <button
                              type="button"
                              className="dashboard-view__button"
                              onClick={() => handleRefreshCollabOps('errors')}
                              disabled={manualDisabled || !collabOpsDetail.counts.error}
                            >
                              {t('controls.retryErrors')}
                            </button>
                          </div>
                        </div>
                      </div>
                      {collabRefreshMessage && <div className="dashboard-view__ops-notice">{collabRefreshMessage}</div>}
                      {collabRefreshError && <div className="dashboard-view__alert dashboard-view__alert--error">{collabRefreshError}</div>}
                      <div className="dashboard-view__ops-detail-grid">
                        <div>
                          <h4>{t('queue.statesTitle')}</h4>
                          <ul>
                            {['pending', 'fresh', 'stale', 'error', 'frozen'].map(state => (
                              <li key={state}>{t(`queue.states.${state}`)} <strong>{collabOpsDetail.counts[state]}</strong></li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <h4>{t('workload.collabCreators')}</h4>
                          <div className="dashboard-view__ops-list dashboard-view__ops-list--compact">
                            {collabOpsDetail.top_creators.map(creator => (
                              <div key={creator.id}>
                                <span>@{creator.username}</span>
                                <em>{t('workload.summary', { due: creator.due_refresh_count, pieces: creator.deliverables_count })}</em>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="dashboard-view__ops-due-table">
                        <h4>{t('controls.eligiblePosts')}</h4>
                        <div className="dashboard-view__ops-due-head">
                          <span>{t('controls.creator')}</span>
                          <span>{t('controls.type')}</span>
                          <span>{t('controls.state')}</span>
                          <span>{t('controls.lastRefresh')}</span>
                          <span>{t('controls.action')}</span>
                        </div>
                        {collabOpsDetail.due_posts.map(post => (
                          <div key={post.id} className="dashboard-view__ops-due-row">
                            <span>@{post.creator_username}</span>
                            <span>{post.post_type}</span>
                            <span>{t(`queue.states.${post.state}`)}</span>
                            <span>{dateTime(post.last_refreshed_at)}</span>
                            <button
                              type="button"
                              className="dashboard-view__row-action"
                              onClick={() => handleRefreshPost(post.id)}
                              disabled={manualDisabled}
                            >
                              {t('controls.refreshPost')}
                            </button>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              <div className="dashboard-view__pause-panel">
                <div>
                  <h3>{t('controls.pauseTitle')}</h3>
                  <p>{t('controls.pauseHelp')}</p>
                  {control.noncritical_paused && (
                    <span className="dashboard-view__pause-current">
                      {t('controls.pausedBy', { user: control.paused_by, date: dateTime(control.paused_until) })}
                    </span>
                  )}
                </div>
                {control.can_manage ? (
                  <div className="dashboard-view__pause-form">
                    <label>
                      {t('controls.reason')}
                      <input
                        type="text"
                        value={pauseReason}
                        maxLength={500}
                        onChange={event => setPauseReason(event.target.value)}
                        placeholder={t('controls.reasonPlaceholder')}
                      />
                    </label>
                    <label>
                      {t('controls.duration')}
                      <select value={pauseDuration} onChange={event => setPauseDuration(Number(event.target.value))}>
                        {[15, 30, 60, 240, 1440].map(value => (
                          <option value={value} key={value}>{formatDuration(value * 60, t)}</option>
                        ))}
                      </select>
                    </label>
                    <button
                      type="button"
                      className={`dashboard-view__button ${control.noncritical_paused ? '' : 'dashboard-view__button--danger'}`}
                      onClick={() => handlePause(control.noncritical_paused ? 'resume' : 'pause')}
                      disabled={pauseLoading}
                    >
                      {control.noncritical_paused ? <PlayCircle size={16} /> : <PauseCircle size={16} />}
                      {t(control.noncritical_paused ? 'controls.resume' : 'controls.pause')}
                    </button>
                  </div>
                ) : (
                  <p className="dashboard-view__muted">{t('controls.superuserOnly')}</p>
                )}
              </div>

              <details className="dashboard-view__audit">
                <summary>{t('audit.title')}</summary>
                <div>
                  {control.audit_events?.length ? control.audit_events.map(event => (
                    <article key={event.id}>
                      <strong>{t(`audit.actions.${event.action}`)}</strong>
                      <span>{event.actor || t('audit.system')} · {dateTime(event.created_at)}</span>
                      <p>{event.reason}</p>
                    </article>
                  )) : <p>{t('audit.empty')}</p>}
                </div>
              </details>
            </motion.section>
          </div>
        </div>
      </motion.div>

      {confirmation && (
        <div className="modal-luxury-overlay" role="presentation">
          <section className="modal-luxury-content admin-confirm-dialog" role="dialog" aria-modal="true" aria-labelledby="admin-confirm-title">
            <div className="modal-luxury-header">
              <h2 id="admin-confirm-title">{t('common.confirm')}</h2>
              <button type="button" className="close-btn-circle" aria-label={t('common.cancel')} onClick={() => setConfirmation(null)}>×</button>
            </div>
            <div className="modal-luxury-body"><p>{confirmation.message}</p></div>
            <div className="modal-luxury-footer">
              <button type="button" className="admin-btn admin-btn--outline" onClick={() => setConfirmation(null)}>{t('common.cancel')}</button>
              <button type="button" className="admin-btn admin-btn--accent" onClick={() => { const action = confirmation.onConfirm; setConfirmation(null); action(); }}>{t('common.confirm')}</button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default MetaManagement;
